import { registerSW } from 'virtual:pwa-register'

/**
 * Shared state for the installable web app (service worker + install prompt).
 *
 * This module is imported once from `main.jsx`, before React renders, because the browser can
 * fire `beforeinstallprompt` as soon as the page loads.
 */

const listeners = new Set()
const INSTALLED_FLAG_KEY = 'm1nt-pwa-installed'

function readInstalledFlag() {
  try {
    return window.localStorage.getItem(INSTALLED_FLAG_KEY) === '1'
  } catch {
    return false
  }
}

function writeInstalledFlag() {
  try {
    window.localStorage.setItem(INSTALLED_FLAG_KEY, '1')
  } catch {
    // Storage can be blocked (private mode); the flag is only a hint.
  }
}

let state = {
  /** A new version was downloaded and waits for the user to reload. */
  updateReady: false,
  /** The browser offered an install prompt (Chromium on desktop and Android). */
  installPromptAvailable: false,
  /** The app is running as an installed web app. */
  installed: false,
  /**
   * This browser installed the app earlier. Chrome then stops offering the install dialog, so the
   * page tells the user to open the installed app instead of showing a dead install button.
   */
  installedBefore: false,
}

let deferredInstallPrompt = null
let applyUpdate = null

function emit(patch) {
  // A new object each time, so React's useSyncExternalStore sees the change and re-renders.
  state = { ...state, ...patch }
  listeners.forEach((listener) => listener())
}

function isStandalone() {
  if (typeof window === 'undefined') return false
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.matchMedia?.('(display-mode: window-controls-overlay)').matches ||
    window.navigator.standalone === true
  )
}

export function subscribePwa(listener) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getPwaSnapshot() {
  return state
}

/** Reloads onto the new version once the user agrees. */
export function reloadForUpdate() {
  applyUpdate?.(true)
}

/** Opens the native install dialog. Resolves to "accepted", "dismissed" or "unavailable". */
export async function promptInstall() {
  if (!deferredInstallPrompt) return 'unavailable'
  const promptEvent = deferredInstallPrompt
  deferredInstallPrompt = null
  emit({ installPromptAvailable: false })
  promptEvent.prompt()
  const { outcome } = await promptEvent.userChoice
  if (outcome === 'accepted') {
    writeInstalledFlag()
    emit({ installedBefore: true })
  }
  return outcome
}

export function initPwa() {
  if (typeof window === 'undefined') return

  emit({ installed: isStandalone(), installedBefore: readInstalledFlag() })

  // Chrome can report an app installed from this same site, even outside the installed window.
  navigator
    .getInstalledRelatedApps?.()
    .then((apps) => {
      if (apps?.length) emit({ installedBefore: true })
    })
    .catch(() => {})

  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault()
    deferredInstallPrompt = event
    emit({ installPromptAvailable: true })
  })

  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null
    writeInstalledFlag()
    emit({ installed: true, installedBefore: true, installPromptAvailable: false })
  })

  window.matchMedia?.('(display-mode: standalone)').addEventListener?.('change', (event) => {
    emit({ installed: event.matches })
  })

  if ('serviceWorker' in navigator) {
    applyUpdate = registerSW({
      immediate: true,
      onNeedRefresh() {
        emit({ updateReady: true })
      },
      onRegisterError(error) {
        console.error('Service worker registration failed:', error)
      },
    })
  }
}
